import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import {
  Controller,
  type Control,
  type FieldErrors,
  type FieldValues,
  type Path,
  type UseFormRegister,
  type UseFormWatch,
} from 'react-hook-form';

import { Field } from '@/components/ui/Field';
import { GEOGRAPHIC_SCOPES, STATE_CODES, scopeNeedsStates } from '@/lib/organization';

/** The three field names this group registers, which differ per form. */
export interface GeographicScopeFieldNames<T extends FieldValues> {
  scope: Path<T>;
  states: Path<T>;
  note: Path<T>;
}

interface GeographicScopeFieldsProps<T extends FieldValues> {
  names: GeographicScopeFieldNames<T>;
  control: Control<T>;
  register: UseFormRegister<T>;
  /** Read so the states box can say whether it is required for this scope. */
  watch: UseFormWatch<T>;
  errors: FieldErrors<T>;
  disabled?: boolean;
}

/** Reads one flat field's message. */
function messageFor<T extends FieldValues>(
  errors: FieldErrors<T>,
  name: Path<T>,
): string | undefined {
  const error = errors[name as keyof FieldErrors<T>];
  const message = (error as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' ? message : undefined;
}

/**
 * Where an organization works: a scope, the states it covers, and a note for
 * anything smaller than a state.
 *
 * The three belong together because the middle one depends on the first: a
 * scope of "state" with no state named is not a scope at all, and the box has
 * to say so before a save is attempted. Written once because Activist staff and
 * a Super Admin both set it, on different pages.
 */
export function GeographicScopeFields<T extends FieldValues>({
  names,
  control,
  register,
  watch,
  errors,
  disabled,
}: GeographicScopeFieldsProps<T>) {
  const statesMatter = scopeNeedsStates(watch(names.scope));

  return (
    <>
      {/* Controlled rather than registered. A select given a defaultValue reads
          it once, when the form first appears, so a reset after a save would
          leave it showing the choice it started with. */}
      <Controller
        name={names.scope}
        control={control}
        render={({ field }) => (
          <Field label="Geographic scope" error={messageFor(errors, names.scope)}>
            <TextField select {...field} value={field.value ?? ''} disabled={disabled}>
              <MenuItem value="">Not set</MenuItem>
              {GEOGRAPHIC_SCOPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Field>
        )}
      />

      {/*
        Not wrapped in Field, unlike every other control here. Field attaches
        the label by cloning its direct child, and that child would be the
        Controller, which ignores it - leaving this dropdown with no visible
        label and no accessible name. It carries its own instead.
      */}
      <Controller
        name={names.states}
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            select
            label="States"
            value={field.value ?? []}
            disabled={disabled}
            onChange={(event) =>
              field.onChange(
                typeof event.target.value === 'string'
                  ? event.target.value.split(',')
                  : event.target.value,
              )
            }
            error={Boolean(fieldState.error)}
            helperText={
              fieldState.error?.message ??
              (statesMatter ? 'Required for this scope.' : 'Optional.')
            }
            slotProps={{
              select: {
                multiple: true,
                renderValue: (selected: unknown) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((code) => (
                      <Chip key={code} label={code} size="small" />
                    ))}
                  </Box>
                ),
                MenuProps: { slotProps: { paper: { sx: { maxHeight: 320 } } } },
              },
            }}
          >
            {STATE_CODES.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <Field
        label="Scope note"
        hint="For anything below state level, such as Greater Baltimore."
        error={messageFor(errors, names.note)}
      >
        <TextField {...register(names.note)} disabled={disabled} />
      </Field>
    </>
  );
}
